import React from 'react';
import { Alert, Box, Button, CircularProgress, Divider, InputAdornment, TextField, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import FleetSummaryCards from '../map/components/auto-fleet-generator/FleetSummaryCards';
import FleetTimelineVisualization from '../map/components/auto-fleet-generator/FleetTimelineVisualization';
import { parseTime } from '../map/components/auto-fleet-generator/timeUtils';
import type { WizardFleetHelpResult, WizardTimelineSlotStatus } from './types';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors } from '../../theme/opsTokens';

interface FleetSupplyStepProps {
  vehicleCount: number;
  capacityStandard: number;
  capacityWheelchair: number;
  onVehicleCountChange: (value: number) => void;
  onCapacityStandardChange: (value: number) => void;
  onCapacityWheelchairChange: (value: number) => void;
  canOpenHelpMe: boolean;
  helpMeStatus: 'idle' | 'loading' | 'ready' | 'error';
  onOpenHelpMe: () => void;
  helpMeError: string | null;
  helpResult: WizardFleetHelpResult | null;
  manualPreview: WizardFleetHelpResult | null;
  fleetMode: 'manual' | 'wizard_helper' | 'none';
  authoritativeFleetId: string | null;
  validationError?: string;
  onTimelineSlotToggle?: (vehicleId: string, blockStartMinutes: number, status: WizardTimelineSlotStatus) => void;
}

export default function FleetSupplyStep({
  vehicleCount,
  capacityStandard,
  capacityWheelchair,
  onVehicleCountChange,
  onCapacityStandardChange,
  onCapacityWheelchairChange,
  canOpenHelpMe,
  helpMeStatus,
  onOpenHelpMe,
  helpMeError,
  helpResult,
  manualPreview,
  fleetMode,
  authoritativeFleetId,
  validationError,
  onTimelineSlotToggle,
}: FleetSupplyStepProps) {
  const { t } = useTranslation('onboarding');
  const helpDisabled = !canOpenHelpMe || helpMeStatus === 'loading';
  const helperIsAuthoritative =
    fleetMode === 'wizard_helper' &&
    Boolean(helpResult) &&
    Boolean(authoritativeFleetId) &&
    helpResult?.fleetId === authoritativeFleetId;
  const helperWasOverridden = Boolean(helpResult) && !helperIsAuthoritative;
  const authoritativeHelpResult = helperIsAuthoritative ? helpResult : null;
  const visibleFleetSummary = authoritativeHelpResult ?? manualPreview;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('fleet.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('fleet.description')}
      </Typography>

      <Box
        sx={(theme) => ({
          border: `1px solid ${opsColors.neutral.line}`,
          borderRadius: 2,
          backgroundColor: opsColors.neutral.panel,
          p: 2,
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, flexDirection: { xs: 'column', lg: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
              {t('fleet.manualHeading')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <TextField
                label={t('fleet.vehicles')}
                type="number"
                value={vehicleCount}
                inputProps={{ min: 1, step: 1 }}
                onChange={(event) => onVehicleCountChange(Number(event.target.value))}
                size="small"
                sx={{ width: { xs: '100%', sm: 98 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="bus" size={20} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={t('fleet.standardSeats')}
                type="number"
                value={capacityStandard}
                inputProps={{ min: 0, step: 1 }}
                onChange={(event) => onCapacityStandardChange(Number(event.target.value))}
                size="small"
                sx={{ width: { xs: '100%', sm: 126 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon d={ICON_FALLBACKS.EventSeatOutlined} size={20} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={t('fleet.wheelchairSeats')}
                type="number"
                value={capacityWheelchair}
                inputProps={{ min: 0, step: 1 }}
                onChange={(event) => onCapacityWheelchairChange(Number(event.target.value))}
                size="small"
                sx={{ width: { xs: '100%', sm: 132 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="wheelchair-move" size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' } }} />
          <Divider sx={{ display: { xs: 'block', lg: 'none' } }} />

          <Box
            sx={{
              width: { xs: '100%', lg: 500 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.25,
            }}
          >
            <Box>
              <Typography variant="subtitle2">{t('fleet.needHelpHeading')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('fleet.needHelpDescription')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', justifyContent: 'space-between', minWidth: 0 }}>
              <Alert
                severity="success"
                sx={{
                  py: 0,
                  minWidth: 0,
                  visibility: helperIsAuthoritative ? 'visible' : 'hidden',
                  '& .MuiAlert-message': {
                    py: 0.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                {t('fleet.autoGeneratedWillBeUsed')}
              </Alert>
              <Button
                variant="outlined"
                disabled={helpDisabled}
                onClick={onOpenHelpMe}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {helpMeStatus === 'loading' ? <CircularProgress size={18} color="inherit" /> : t('fleet.helpMe')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {validationError ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {validationError}
        </Alert>
      ) : null}

      {helpMeError ? (
        <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
          {helpMeError}
        </Typography>
      ) : null}

      {helperWasOverridden ? (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {t('fleet.helperOverridden')}
        </Alert>
      ) : null}

      {visibleFleetSummary ? (
        <Box sx={{ mt: 2 }}>
          <FleetSummaryCards summary={visibleFleetSummary.summary} mode="trip-layer" />
          <FleetTimelineVisualization
            vehicles={visibleFleetSummary.shifts}
            serviceStartMinutes={parseTime(visibleFleetSummary.serviceStartTime)}
            serviceEndMinutes={parseTime(visibleFleetSummary.serviceEndTime)}
            intervalMinutes={30}
            peakPeriods={visibleFleetSummary.peakPeriods}
            interactive={Boolean(onTimelineSlotToggle)}
            onBlockClick={onTimelineSlotToggle}
          />
          {onTimelineSlotToggle ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('fleet.timelineHint')}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
